import { useState } from "react";

import { skipToken } from "@apollo/client";
import { Spinner } from "@blueprintjs/core";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { toast } from "sonner";

import { buildUrl } from "@/components/helpers/url-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { isXsToMdQuery, Media } from "@/components/layout/Media";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { AffirmTag } from "@/components/pieces/buy-credits/AffirmTag";
import { BackButton } from "@/components/pieces/onboarding/BackButton";
import { Header } from "@/components/pieces/onboarding/Header";
import {
  ComponentTypeEnum,
  useChooseCoachIntroProductSuspenseQuery,
  useProgramComponentQuerySuspenseQuery,
  useSaveIntakeFormMutation,
} from "@/graphql";
import { formatErrors } from "@/utils/array";

import { SelectCoach } from "./SelectCoach";

const DEFAULT_SPECIALTY = "job-search-coaching";

function AuthChoose() {
  const navigate = useNavigate();
  const location = useLocation();
  const { oid, specialtySlug } = useParams();
  const [searchParams] = useSearchParams();
  const back = searchParams.get("back");
  const programComponentId = searchParams.get("programComponentId");
  const [isNavigating, setIsNavigating] = useState(false);

  // get the intro session from master program component
  const { data: programComponent } = useProgramComponentQuerySuspenseQuery(
    programComponentId
      ? { variables: { programComponentId: programComponentId } }
      : skipToken,
  );
  const introSession =
    programComponent?.programComponent?.childComponents?.find(
      (programComponent) =>
        programComponent.type === ComponentTypeEnum.OneOneCoachingIntro,
    );

  const { data: introProductData } = useChooseCoachIntroProductSuspenseQuery({
    variables: {
      specialtySlug: specialtySlug || DEFAULT_SPECIALTY,
    },
  });
  const currentUser = introProductData?.currentUser;
  const product = introProductData?.introProduct;
  const intakeFormToken = currentUser?.intakeForm?.token;

  // if no current user or product, redirect to choose page
  if (!currentUser || !product) {
    const url = buildUrl(`/choose`, {
      data: { ...useParams() },
    });
    return <Navigate to={url} />;
  }

  const [saveIntakeForm, { loading: saving }] = useSaveIntakeFormMutation({
    update: (cache) => {
      cache.evict({ fieldName: "agentProfiles" });
      cache.gc();
    },
  });

  const showError = (message: string) => {
    toast.error("Failed to choose coach", {
      description: message,
    });
  };

  async function onSelectCoach(coachId: string) {
    const sessionId = introSession?.id;
    const productSlug = !sessionId ? product?.slug : undefined;

    try {
      const result = await saveIntakeForm({
        variables: {
          input: { coachId },
          token: intakeFormToken,
          orderId: oid,
          productSlug: product?.slug,
        },
      });

      // Check for GraphQL errors in the response
      const { errors } = result.data?.saveIntakeForm ?? {};
      if (errors?.length) {
        const errorMessage = formatErrors(errors);
        showError(errorMessage);
        return;
      }

      // Set navigating state to true before navigation
      setIsNavigating(true);

      const scheduleUrl = buildUrl(`/schedule/${coachId}`, {
        data: {
          productSlug,
          programComponentId: sessionId,
          match: true,
          back: location.pathname + location.search,
        },
      });
      navigate(scheduleUrl, { replace: true });
    } catch {
      setIsNavigating(false); // Reset on error
      showError("Please try again. If the issue persists, contact support.");
    }
  }

  const backButton = back && (
    <BackButton
      hoverMessage="Go Back"
      onClick={() => {
        navigate(back);
      }}
      mediaQuery={isXsToMdQuery}
      mt={[0, 12]}
    />
  );

  return (
    <>
      <Media at="xs">
        <Header linkToDash={true} backButton={backButton} />
      </Media>
      <Media greaterThan="xs">
        <Header linkToDash={true} />
      </Media>
      <Box my={[70, 110, 130]} px={[3, 3, 0]} style={{ position: "relative" }}>
        {saving || isNavigating ? (
          <Flex justifyContent="center">
            <Spinner size={24} />
          </Flex>
        ) : (
          <>
            <Media greaterThan="xs">{backButton}</Media>
            <SelectCoach
              intakeFormToken={intakeFormToken}
              loading={saving || isNavigating}
              product={product}
              onSelectCoach={onSelectCoach}
              includeCurrent={true}
              programComponentId={programComponentId || ""}
            />
          </>
        )}
      </Box>
    </>
  );
}

export function ChoosePage() {
  return (
    <Authenticate layout="compact" showMessageBar={false} maxWidth={"100%"}>
      <AuthChoose />
      <AffirmTag />
    </Authenticate>
  );
}
